import { PatientDetailsDto } from '../../../types/swaggerTypes'
import {
  PatientOrderStatus,
  normalizePatientOrderStatus
} from '../PatientDashboard/Patient.model'

type PtaValidationPatient = Pick<
  PatientDetailsDto,
  'patientConsentCaptured' | 'patientBaselineCaptured' | 'patientOrderStatus'
>

const PTA_ORDER_CONTINUATION_STATUSES = [
  PatientOrderStatus.IncompletePAF,
  PatientOrderStatus.IncompleteCheckout
]

const isPtaOrderContinuationStatus = (normalizedStatus: string): boolean =>
  PTA_ORDER_CONTINUATION_STATUSES.includes(
    normalizedStatus as PatientOrderStatus
  )

/** True when eConsent webhook (or API) reports consent captured for PTA. */
export function hasPtaConsentCaptured(
  patient: Pick<PatientDetailsDto, 'patientConsentCaptured'>
): boolean {
  return patient.patientConsentCaptured === true
}

type PtaUiSyncPatient = Pick<
  PatientDetailsDto,
  | 'patientConsentCaptured'
  | 'patientBaselineCaptured'
  | 'patientOrderStatus'
  | 'castorEConsentUrl'
  | 'castorEdcUrl'
>

/**
 * CLOS-13951 — Fields that drive visible PTA Patient Details UI after Castor return.
 * Silent background polls must not commit React state (and risk a visible refresh)
 * until one of these webhook/API-driven values actually changes.
 */
export function havePtaPatientDetailUiFieldsChanged(
  previous: PtaUiSyncPatient | null | undefined,
  next: PtaUiSyncPatient
): boolean {
  if (!previous) {
    return true
  }

  return (
    previous.patientConsentCaptured !== next.patientConsentCaptured ||
    previous.patientBaselineCaptured !== next.patientBaselineCaptured ||
    normalizePatientOrderStatus(previous.patientOrderStatus) !==
      normalizePatientOrderStatus(next.patientOrderStatus) ||
    (previous.castorEConsentUrl ?? '') !== (next.castorEConsentUrl ?? '') ||
    (previous.castorEdcUrl ?? '') !== (next.castorEdcUrl ?? '')
  )
}

/**
 * CLOS-13916 — True when the patient is still waiting for the async eConsent
 * webhook to persist consent attributes (bounded polling candidate).
 */
export function isPtaConsentSyncPending(
  patient:
    | Pick<PatientDetailsDto, 'patientConsentCaptured' | 'patientOrderStatus'>
    | null
    | undefined,
  isPostTrialAccess: boolean
): boolean {
  if (!isPostTrialAccess || !patient) {
    return false
  }

  if (hasPtaConsentCaptured(patient)) {
    return false
  }

  return (
    normalizePatientOrderStatus(patient.patientOrderStatus) ===
    PatientOrderStatus.AwaitingConsent
  )
}

/** True when baseline information has been captured for PTA. */
export function hasPtaBaselineCaptured(
  patient: Pick<PatientDetailsDto, 'patientBaselineCaptured'>
): boolean {
  return patient.patientBaselineCaptured === true
}

/**
 * True when Step 2 "Submit baseline info" may open Castor EDC.
 * Consent must already be captured (CLOS-14064 demo finding #1). The Patient
 * Details API contract defines a `null` / omitted baseline value as not yet
 * captured, so only an explicit `true` blocks the CTA.
 */
export function canSubmitPtaBaselineInfo(
  patient: Pick<
    PatientDetailsDto,
    'patientConsentCaptured' | 'patientBaselineCaptured'
  >
): boolean {
  return (
    hasPtaConsentCaptured(patient) && patient.patientBaselineCaptured !== true
  )
}

/** True when both consent and baseline are explicitly captured for PTA. */
export function isPtaBeginOrderReady(patient: PtaValidationPatient): boolean {
  return hasPtaConsentCaptured(patient) && hasPtaBaselineCaptured(patient)
}

/**
 * Resolve contradictory PTA pre-order data in favour of the prerequisite
 * signals. Consent is a hard gate. A confirmed baseline (persisted, or via
 * getProgress) keeps Begin your order even while the Oracle baseline flag lags,
 * so the status pill matches the patients list and the header CTA.
 */
export function resolvePtaPreOrderStatus(
  patient: PtaValidationPatient,
  isPostTrialAccess: boolean,
  baselineProgressConfirmed = false
): string {
  if (!isPostTrialAccess) {
    return patient.patientOrderStatus ?? ''
  }

  const normalizedStatus = normalizePatientOrderStatus(
    patient.patientOrderStatus
  )

  // CLOS-14505 — an in-progress order never outranks the consent gate.
  if (isPtaOrderContinuationStatus(normalizedStatus)) {
    return hasPtaConsentCaptured(patient)
      ? normalizedStatus
      : PatientOrderStatus.AwaitingConsent
  }

  if (
    ![
      PatientOrderStatus.AwaitingConsent,
      PatientOrderStatus.AwaitingBaselineData,
      PatientOrderStatus.BeginOrder
    ].includes(normalizedStatus as PatientOrderStatus)
  ) {
    return normalizedStatus
  }

  if (!hasPtaConsentCaptured(patient)) {
    return PatientOrderStatus.AwaitingConsent
  }

  if (baselineProgressConfirmed) {
    return PatientOrderStatus.BeginOrder
  }

  if (!hasPtaBaselineCaptured(patient)) {
    return PatientOrderStatus.AwaitingBaselineData
  }

  return PatientOrderStatus.BeginOrder
}

/**
 * Disable header order CTAs (Begin order, Request resupply) for PTA patients
 * until consent and baseline are both captured.
 */
export function shouldDisablePtaBeginOrder(
  patient: PtaValidationPatient,
  isPostTrialAccess: boolean,
  baselineProgressConfirmed = false
): boolean {
  if (!isPostTrialAccess) {
    return false
  }

  // Local getProgress confirmation may unlock Begin Order after consent is
  // captured, even when patientBaselineCaptured has not yet been echoed.
  if (baselineProgressConfirmed && hasPtaConsentCaptured(patient)) {
    return false
  }

  const normalizedStatus = normalizePatientOrderStatus(
    patient.patientOrderStatus
  )

  // An in-progress order has already passed the baseline step, so only the
  // consent hard gate is re-checked (CLOS-14505).
  if (isPtaOrderContinuationStatus(normalizedStatus)) {
    return !hasPtaConsentCaptured(patient)
  }

  return !isPtaBeginOrderReady(patient)
}
